import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield, BarChart3, Megaphone } from "lucide-react";
import { useConsent } from "../lib/consent";

/**
 * CookieBanner — GDPR/CCPA-compliant first-visit consent strip.
 *
 * • Renders a bottom-pinned banner on first visit (no choice stored).
 * • Three actions: Accept all / Reject all / Customize.
 * • "Customize" opens an inline preferences sheet where the user can
 *   toggle Analytics / Marketing independently (Necessary is forced on).
 * • The same sheet can be reopened anytime via the footer's "Cookie
 *   settings" link (controlled by `useConsent().openSettings()`).
 *
 * Styling matches the rest of the QorTrace surface: opaque dark card,
 * cyan accents, pill buttons.
 */
export default function CookieBanner() {
  const {
    preferences, hasDecided, settingsOpen,
    acceptAll, rejectAll, update, openSettings, closeSettings,
  } = useConsent();

  const showBanner = !hasDecided && !settingsOpen;
  const showSettings = settingsOpen || (!hasDecided && false);

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {showBanner && (
        <FirstVisitBanner
          onAccept={acceptAll}
          onReject={rejectAll}
          onCustomize={openSettings}
        />
      )}
      {showSettings && (
        <SettingsSheet
          initialPreferences={preferences}
          onClose={closeSettings}
          onSave={update}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
        />
      )}
    </>
  );
}

function FirstVisitBanner({ onAccept, onReject, onCustomize }) {
  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="mx-auto max-w-[1100px] rounded-2xl border p-5 sm:p-6"
        style={{
          pointerEvents: "auto",
          background: "#050B14",
          borderColor: "rgba(122,231,242,0.28)",
          boxShadow: "0 10px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(122,231,242,0.08)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex items-start gap-3 flex-1">
            <Cookie size={20} style={{ color: "#7AE7F2" }} className="mt-1 shrink-0" />
            <div>
              <div
                className="qt-mono text-xs uppercase tracking-[0.2em] mb-1.5"
                style={{ color: "#7AE7F2" }}
              >
                Cookies &amp; privacy
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                We use strictly-necessary cookies to run the app. With your
                consent we also use analytics cookies to understand how
                QorTrace is used so we can improve it.{" "}
                <Link
                  to="/legal/cookies"
                  className="underline"
                  style={{ color: "#7AE7F2" }}
                  data-testid="cookie-banner-policy-link"
                >
                  Cookie Policy
                </Link>{" "}
                &middot;{" "}
                <Link
                  to="/legal/privacy"
                  className="underline"
                  style={{ color: "#7AE7F2" }}
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 md:shrink-0">
            <button
              type="button"
              data-testid="cookie-banner-reject-btn"
              onClick={onReject}
              className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
              style={{
                color: "rgba(255,255,255,0.9)",
                borderColor: "rgba(255,255,255,0.2)",
                background: "transparent",
              }}
            >
              Reject all
            </button>
            <button
              type="button"
              data-testid="cookie-banner-customize-btn"
              onClick={onCustomize}
              className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
              style={{
                color: "rgba(255,255,255,0.9)",
                borderColor: "rgba(122,231,242,0.35)",
                background: "transparent",
              }}
            >
              Customize
            </button>
            <button
              type="button"
              data-testid="cookie-banner-accept-btn"
              onClick={onAccept}
              className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
              style={{ background: "#7AE7F2", color: "#001018" }}
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsSheet({ initialPreferences, onClose, onSave, onAcceptAll, onRejectAll }) {
  const [analytics, setAnalytics] = useState(Boolean(initialPreferences?.analytics));
  const [marketing, setMarketing] = useState(Boolean(initialPreferences?.marketing));

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-settings-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      style={{ background: "rgba(0,4,10,0.92)" }}
    >
      <div
        className="relative w-full max-w-[640px] rounded-2xl border p-6 sm:p-8"
        style={{
          background: "#050B14",
          borderColor: "rgba(122,231,242,0.28)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="cookie-settings-close-btn"
          className="absolute top-4 right-4 p-1 rounded-full transition-colors"
          style={{ color: "rgba(255,255,255,0.6)" }}
        >
          <X size={18} />
        </button>

        <div
          className="qt-mono text-xs uppercase tracking-[0.25em] mb-2"
          style={{ color: "#7AE7F2" }}
        >
          Cookie preferences
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Manage your cookies</h2>
        <p className="text-sm text-white/70 mb-6">
          You can change your mind at any time by clicking <em>Cookie settings</em>{" "}
          in our footer. For full detail, read our{" "}
          <Link to="/legal/cookies" className="underline" style={{ color: "#7AE7F2" }}>
            Cookie Policy
          </Link>
          .
        </p>

        <div className="space-y-3 mb-6">
          <CategoryRow
            icon={<Shield size={16} />}
            title="Strictly necessary"
            description="Required for login, session management, CSRF protection, and rate limiting. Always on."
            checked
            disabled
            testid="cookie-cat-necessary"
          />
          <CategoryRow
            icon={<BarChart3 size={16} />}
            title="Analytics"
            description="Anonymous product analytics via PostHog so we can understand usage and improve QorTrace. Masks all form inputs and wallet addresses."
            checked={analytics}
            onChange={setAnalytics}
            testid="cookie-cat-analytics"
          />
          <CategoryRow
            icon={<Megaphone size={16} />}
            title="Marketing"
            description="Reserved for future advertising and retargeting tags. QorTrace uses none today."
            checked={marketing}
            onChange={setMarketing}
            testid="cookie-cat-marketing"
          />
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onRejectAll}
            data-testid="cookie-settings-reject-all-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold border"
            style={{
              color: "rgba(255,255,255,0.9)",
              borderColor: "rgba(255,255,255,0.2)",
              background: "transparent",
            }}
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={() => onSave({ analytics, marketing })}
            data-testid="cookie-settings-save-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold border"
            style={{
              color: "rgba(255,255,255,0.9)",
              borderColor: "rgba(122,231,242,0.35)",
              background: "transparent",
            }}
          >
            Save choices
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            data-testid="cookie-settings-accept-all-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold"
            style={{ background: "#7AE7F2", color: "#001018" }}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon, title, description, checked, onChange, disabled, testid }) {
  return (
    <div
      className="flex items-start gap-3 p-4 rounded-lg border"
      style={{
        background: "rgba(122,231,242,0.03)",
        borderColor: "rgba(122,231,242,0.14)",
      }}
      data-testid={testid}
    >
      <div style={{ color: "#7AE7F2" }} className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-white mb-1">{title}</div>
        <div className="text-xs text-white/60 leading-relaxed">{description}</div>
      </div>
      <div className="shrink-0 mt-0.5">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            disabled={disabled}
            onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
            data-testid={`${testid}-toggle`}
          />
          <span
            className="relative inline-block w-9 h-5 rounded-full transition-colors"
            style={{
              background: checked ? "#7AE7F2" : "rgba(255,255,255,0.18)",
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform"
              style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}
            />
          </span>
        </label>
      </div>
    </div>
  );
}
